import { el, escape } from './dom';
import type { DispatchValue } from '../pipeline/dispatch';
import type { TextDetection } from '../detect/pii/types';
import type { RedactionStore, StoreSnapshot } from '../lib/redactionStore';
import type { NormalizedRect } from './types';
import { drawTextDetections, drawManualRects } from '../lib/preview/render';
import {
  hotspotsForTextEntries,
  hotspotsForManualEntries,
  type Hotspot,
} from '../lib/preview/hotspots';
import { installRedactionHover } from './redactionHover';
import { PdfPageCache } from '../lib/preview/pdfPageCache';
import { drawBlurRect } from '../redact/image';
import { loadPdfjs } from '../pipeline/pdf';

const PREVIEW_SCALE_DESKTOP = 1.5;
const PREVIEW_SCALE_MOBILE = 1.2;
/** Below this CSS-pixel size, ignore the drag — likely a click slip. */
import { isSmallViewport } from '../lib/device';

const MIN_DRAG_PX = 4;

const isMobile = (): boolean => isSmallViewport();

const padFaceBbox = (
  bbox: { x: number; y: number; w: number; h: number },
  width: number,
  height: number,
  factor = 0.3,
): { x: number; y: number; w: number; h: number } => {
  const dx = bbox.w * factor;
  const dy = bbox.h * factor;
  const x = Math.max(0, bbox.x - dx);
  const y = Math.max(0, bbox.y - dy);
  const w = Math.min(width - x, bbox.w + 2 * dx);
  const h = Math.min(height - y, bbox.h + 2 * dy);
  return { x, y, w, h };
};

export type PreviewCanvas = {
  root: HTMLElement;
  dispose(): void;
};

const renderImage = (
  canvas: HTMLCanvasElement,
  value: Extract<DispatchValue, { kind: 'image' }>,
  store: RedactionStore,
): void => {
  canvas.width = value.bitmap.width;
  canvas.height = value.bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(value.bitmap, 0, 0);
  for (const face of store.selectedFaces()) {
    const padded = padFaceBbox(face.bbox, canvas.width, canvas.height);
    const blurPx = Math.max(48, Math.round(Math.max(padded.w, padded.h) * 0.18));
    drawBlurRect(ctx, value.bitmap, padded, blurPx);
  }
  drawManualRects(ctx, store.selectedManualsOn(0), canvas.width, canvas.height);
};

const renderTextPreview = (
  host: HTMLElement,
  value: Extract<DispatchValue, { kind: 'text' | 'csv' }>,
  store: RedactionStore,
): void => {
  const text = value.originalText;
  const selected = store
    .snapshotSelection()
    .textPii.slice()
    .sort((a, b) => a.start - b.start || a.end - b.end);

  let html = '';
  let cursor = 0;
  for (const d of selected) {
    if (d.start < cursor) continue;
    if (d.start < 0 || d.end > text.length || d.end <= d.start) continue;
    html += escape(text.slice(cursor, d.start));
    html += `<span class="redacted">${escape(text.slice(d.start, d.end))}</span>`;
    cursor = d.end;
  }
  html += escape(text.slice(cursor));
  host.innerHTML = html;
};

const buildPageNav = (store: RedactionStore): HTMLElement => {
  const nav = el(`
    <div class="preview-pagenav">
      <button type="button" class="preview-page-btn" data-action="prev" aria-label="previous page">←</button>
      <span class="preview-page-label" data-page-label></span>
      <button type="button" class="preview-page-btn" data-action="next" aria-label="next page">→</button>
    </div>
  `);
  nav.querySelector<HTMLButtonElement>('[data-action="prev"]')?.addEventListener('click', () => {
    store.setPage(store.snapshot().currentPage - 1);
  });
  nav.querySelector<HTMLButtonElement>('[data-action="next"]')?.addEventListener('click', () => {
    store.setPage(store.snapshot().currentPage + 1);
  });
  return nav;
};

const updatePageNav = (nav: HTMLElement, snap: StoreSnapshot): void => {
  const label = nav.querySelector<HTMLElement>('[data-page-label]');
  if (label) label.textContent = `page ${String(snap.currentPage + 1)} of ${String(snap.pageCount)}`;
  const prev = nav.querySelector<HTMLButtonElement>('[data-action="prev"]');
  const next = nav.querySelector<HTMLButtonElement>('[data-action="next"]');
  if (prev) prev.disabled = snap.currentPage <= 0;
  if (next) next.disabled = snap.currentPage >= snap.pageCount - 1;
};

const buildSpinner = (): HTMLElement =>
  el(`<div class="preview-spinner mono">rendering page…</div>`);

/**
 * Manual-mode controller. Wires up pointer drag on the given canvas:
 * shows a dashed-red overlay that follows the cursor, and on mouseup
 * adds a normalized rectangle to the store for the visible page. Used
 * for both image and PDF preview canvases. Returns a dispose handle.
 */
const wireManualMode = (
  wrap: HTMLElement,
  canvas: HTMLCanvasElement,
  store: RedactionStore,
  state: { active: boolean },
): (() => void) => {
  const dragRect = el(`<div class="preview-drag-rect" hidden></div>`);
  wrap.append(dragRect);

  let dragStart: { x: number; y: number } | null = null;
  let pointerId: number | null = null;
  let pageAtDrag = 0;

  const positionRect = (x: number, y: number, w: number, h: number): void => {
    dragRect.style.left = `${String(x)}px`;
    dragRect.style.top = `${String(y)}px`;
    dragRect.style.width = `${String(w)}px`;
    dragRect.style.height = `${String(h)}px`;
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (!state.active) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    dragStart = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    pointerId = e.pointerId;
    pageAtDrag = store.snapshot().currentPage;
    canvas.setPointerCapture(e.pointerId);
    dragRect.hidden = false;
    positionRect(dragStart.x, dragStart.y, 0, 0);
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!dragStart || pointerId !== e.pointerId) return;
    const rect = canvas.getBoundingClientRect();
    const cx = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const cy = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
    const x = Math.min(dragStart.x, cx);
    const y = Math.min(dragStart.y, cy);
    const w = Math.abs(cx - dragStart.x);
    const h = Math.abs(cy - dragStart.y);
    positionRect(x, y, w, h);
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!dragStart || pointerId !== e.pointerId) return;
    const rect = canvas.getBoundingClientRect();
    const cx = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const cy = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
    const x = Math.min(dragStart.x, cx);
    const y = Math.min(dragStart.y, cy);
    const w = Math.abs(cx - dragStart.x);
    const h = Math.abs(cy - dragStart.y);
    dragStart = null;
    pointerId = null;
    dragRect.hidden = true;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    if (rect.width <= 0 || rect.height <= 0) return;
    if (w < MIN_DRAG_PX || h < MIN_DRAG_PX) return;
    const norm: NormalizedRect = {
      x: x / rect.width,
      y: y / rect.height,
      w: w / rect.width,
      h: h / rect.height,
    };
    store.addManual(pageAtDrag, norm);
  };

  const onPointerCancel = (): void => {
    dragStart = null;
    pointerId = null;
    dragRect.hidden = true;
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerCancel);

  return () => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    dragRect.remove();
  };
};

/**
 * Wire the floating "redact this" button to the user's text selection
 * inside the pdfjs text layer. While the selection is non-empty and
 * lives inside `textLayerEl`, the button positions itself near the end
 * of the selection. Clicking it converts the selection's client rects
 * to normalized [0..1] coordinates, hands them to the store as a
 * manual-text entry, and clears the native selection.
 */
const wireTextSelection = (
  wrap: HTMLElement,
  textLayerEl: HTMLElement,
  btn: HTMLElement,
  store: RedactionStore,
  modeState: { active: boolean },
  proGate: () => boolean,
): (() => void) => {
  const hide = (): void => {
    btn.hidden = true;
  };

  const computeRectsFromSelection = (
    sel: Selection,
  ): { rects: NormalizedRect[]; text: string; lastClient: DOMRect } | null => {
    if (sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!textLayerEl.contains(range.startContainer) || !textLayerEl.contains(range.endContainer)) {
      return null;
    }
    const text = sel.toString().trim();
    if (text.length === 0) return null;
    const wrapRect = wrap.getBoundingClientRect();
    if (wrapRect.width <= 0 || wrapRect.height <= 0) return null;
    const clientRects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0,
    );
    if (clientRects.length === 0) return null;
    const rects = clientRects.map((r) => ({
      x: (r.left - wrapRect.left) / wrapRect.width,
      y: (r.top - wrapRect.top) / wrapRect.height,
      w: r.width / wrapRect.width,
      h: r.height / wrapRect.height,
    }));
    const lastClient = clientRects[clientRects.length - 1] ?? clientRects[0];
    if (!lastClient) return null;
    return { rects, text, lastClient };
  };

  const positionBtn = (lastClient: DOMRect): void => {
    const wrapRect = wrap.getBoundingClientRect();
    btn.style.left = `${String(lastClient.right - wrapRect.left + 8)}px`;
    btn.style.top = `${String(lastClient.top - wrapRect.top)}px`;
    btn.hidden = false;
  };

  const onSelectionChange = (): void => {
    if (modeState.active) {
      hide();
      return;
    }
    const sel = window.getSelection();
    if (!sel) {
      hide();
      return;
    }
    const computed = computeRectsFromSelection(sel);
    if (!computed) {
      hide();
      return;
    }
    positionBtn(computed.lastClient);
  };

  const onClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    if (modeState.active) return;
    // Highlight-to-redact is pro-only. If the gate blocks, we hide the
    // pill but leave the user's selection in place so they can see why
    // nothing happened on screen and decide what to do.
    if (!proGate()) {
      hide();
      return;
    }
    const sel = window.getSelection();
    if (!sel) return;
    const computed = computeRectsFromSelection(sel);
    if (!computed) return;
    const pageIndex = store.snapshot().currentPage;
    store.addManualText(pageIndex, computed.rects, computed.text);
    sel.removeAllRanges();
    hide();
  };

  document.addEventListener('selectionchange', onSelectionChange);
  btn.addEventListener('mousedown', (e) => {
    // Prevent the click from clearing the selection before our handler
    // runs (mousedown on a button outside the selection collapses it).
    e.preventDefault();
  });
  btn.addEventListener('click', onClick);

  return () => {
    document.removeEventListener('selectionchange', onSelectionChange);
    btn.removeEventListener('click', onClick);
  };
};

/** Build the toolbar that toggles manual mode + the helper banner. */
const buildManualToolbar = (
  state: { active: boolean },
  setActive: (on: boolean) => void,
): { toolbar: HTMLElement; banner: HTMLElement } => {
  const toolbar = el(`
    <div class="preview-toolbar">
      <button type="button" class="preview-mode-btn" data-action="manual-toggle" aria-pressed="false">
        + manual redaction
      </button>
    </div>
  `);
  const banner = el(`
    <div class="preview-manual-banner mono" hidden>
      manual redaction mode — click and drag to mark areas. esc to exit.
    </div>
  `);
  toolbar.querySelector<HTMLButtonElement>('[data-action="manual-toggle"]')?.addEventListener(
    'click',
    () => {
      setActive(!state.active);
    },
  );
  return { toolbar, banner };
};

const reflectManualMode = (
  host: HTMLElement,
  toolbar: HTMLElement,
  banner: HTMLElement,
  active: boolean,
): void => {
  host.classList.toggle('manual-mode', active);
  banner.hidden = !active;
  const btn = toolbar.querySelector<HTMLButtonElement>('[data-action="manual-toggle"]');
  if (btn) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
    btn.textContent = active ? '× exit manual mode' : '+ manual redaction';
  }
};

export type PreviewCanvasOpts = {
  /** Called when the user tries a pro-only action without pro.
   *  Return true to allow the action, false to block. The caller is
   *  responsible for showing whatever upgrade UI it wants. */
  requirePro?: () => boolean;
};

export const PreviewCanvas = (
  store: RedactionStore,
  value: DispatchValue,
  opts: PreviewCanvasOpts = {},
): PreviewCanvas => {
  const root = el(`<div class="preview-canvas-host"></div>`);
  let unsubscribe: (() => void) | null = null;
  const cleanups: (() => void)[] = [];
  const proGate = (): boolean => (opts.requirePro ? opts.requirePro() : true);

  const supportsManual = value.kind === 'image' || value.kind === 'pdf';
  const modeState = { active: false };

  if (value.kind === 'image') {
    const wrap = el(`<div class="preview-canvas-wrap"></div>`);
    const canvas = document.createElement('canvas');
    canvas.className = 'preview-canvas';
    wrap.append(canvas);

    const setActive = (on: boolean): void => {
      // Pro gate: turning manual mode on is a pro-only action. Turning
      // it off (already-active session) is always allowed.
      if (on && !modeState.active && !proGate()) return;
      modeState.active = on;
      reflectManualMode(root, toolbar, banner, on);
    };
    const { toolbar, banner } = buildManualToolbar(modeState, setActive);
    root.append(toolbar, banner, wrap);

    cleanups.push(wireManualMode(wrap, canvas, store, modeState));
    cleanups.push(installEscapeExit(modeState, () => {
      setActive(false);
    }));

    // Hover-to-reveal: hovering a redaction surfaces what's under it
    // and clicking removes it from the selection. Manual-mode drag
    // suppresses this so the two pointer flows don't collide.
    const hover = installRedactionHover(wrap, canvas, store, modeState);
    cleanups.push(hover.dispose);

    const render = (): void => {
      renderImage(canvas, value, store);
      // Image path has a single virtual page (index 0). Faces aren't
      // included — they render as blurs, not rects, so the hover
      // affordance is misleading for them.
      hover.setHotspots(hotspotsForManualEntries(store.selectedManualEntriesOn(0)));
    };
    render();
    unsubscribe = store.subscribe(render);
  } else if (value.kind === 'text' || value.kind === 'csv') {
    const pre = el(`<pre class="preview-text"></pre>`);
    root.append(pre);
    const render = (): void => {
      renderTextPreview(pre, value, store);
    };
    render();
    unsubscribe = store.subscribe(render);
  } else {
    // PDF
    const wrap = el(`<div class="preview-canvas-wrap"></div>`);
    const canvas = document.createElement('canvas');
    canvas.className = 'preview-canvas';
    const textLayerEl = el(`<div class="pdf-textlayer"></div>`);
    const redactBtn = el(`
      <button type="button" class="preview-redact-selection" hidden>
        redact this →
      </button>
    `);
    wrap.append(canvas, textLayerEl, redactBtn);

    const setActive = (on: boolean): void => {
      // Pro gate: turning manual mode on is a pro-only action. Turning
      // it off (already-active session) is always allowed.
      if (on && !modeState.active && !proGate()) return;
      modeState.active = on;
      reflectManualMode(root, toolbar, banner, on);
      // Manual drag mode swallows pointer events; text selection
      // mode (the default) gets them via the text layer.
      textLayerEl.classList.toggle('is-disabled', on);
      if (on) hideRedactBtn();
    };
    const { toolbar, banner } = buildManualToolbar(modeState, setActive);
    const spinner = buildSpinner();
    spinner.style.display = 'none';
    const nav = buildPageNav(store);
    root.append(toolbar, banner, spinner, wrap, nav);
    updatePageNav(nav, store.snapshot());

    cleanups.push(wireManualMode(wrap, canvas, store, modeState));
    cleanups.push(installEscapeExit(modeState, () => {
      setActive(false);
    }));

    // Hover-to-reveal: same as the image path. Hotspots get refreshed
    // per page render below.
    const hover = installRedactionHover(wrap, canvas, store, modeState);
    cleanups.push(hover.dispose);

    const scale = isMobile() ? PREVIEW_SCALE_MOBILE : PREVIEW_SCALE_DESKTOP;
    let cache: PdfPageCache | null = null;
    let renderToken = 0;
    let activeTextLayer: { cancel(): void } | null = null;

    const ensureCache = async (): Promise<PdfPageCache> => {
      if (cache) return cache;
      const pdfjs = await loadPdfjs();
      const sourceDoc = await pdfjs.getDocument({ data: value.pdfBytes.slice() }).promise;
      cache = new PdfPageCache(sourceDoc, scale);
      cleanups.push(() => {
        cache?.dispose();
        void sourceDoc.destroy();
      });
      return cache;
    };

    const hideRedactBtn = (): void => {
      redactBtn.hidden = true;
    };

    const render = (): void => {
      const snap = store.snapshot();
      updatePageNav(nav, snap);
      const myToken = ++renderToken;
      const page = value.pages[snap.currentPage];
      if (!page) return;
      spinner.style.display = '';
      hideRedactBtn();
      activeTextLayer?.cancel();
      activeTextLayer = null;
      textLayerEl.replaceChildren();
      void (async (): Promise<void> => {
        const c = await ensureCache();
        const cached = await c.get(snap.currentPage);
        if (myToken !== renderToken) return;
        canvas.width = cached.width;
        canvas.height = cached.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(cached.canvas, 0, 0);
        const selected: readonly TextDetection[] = store.selectedTextOn(page.index);
        drawTextDetections(ctx, page, selected, scale);
        drawManualRects(
          ctx,
          store.selectedManualsOn(page.index),
          canvas.width,
          canvas.height,
        );
        spinner.style.display = 'none';

        // Refresh the hover layer's hotspots for this page. Text PII
        // hotspots from selectedTextEntriesOn line up with the rects
        // drawn by drawTextDetections; manual + OCR hotspots come from
        // selectedManualEntriesOn.
        const textHotspots = hotspotsForTextEntries(
          page,
          store.selectedTextEntriesOn(page.index),
          scale,
          canvas.width,
          canvas.height,
        );
        const manualHotspots = hotspotsForManualEntries(
          store.selectedManualEntriesOn(page.index),
        );
        const allHotspots: Hotspot[] = [...textHotspots, ...manualHotspots];
        hover.setHotspots(allHotspots);

        // Mount the pdfjs text layer so the user can select text. The
        // layer overlays the canvas with absolutely-positioned spans
        // whose color is transparent — selection draws on the spans
        // and we read the resulting Range rects on demand.
        //
        // Scaling: pdfjs's setLayerDimensions sizes the layer to
        // `var(--total-scale-factor) * pageWidth`. If we leave the
        // factor at our render scale (1.5/1.2), the layer ends up at
        // backing-pixel dimensions while the canvas is CSS-scaled by
        // max-width: 100% to fit the column. The mismatch made spans
        // for text near page bottoms intercept clicks on the page-nav
        // arrows below the wrap (showed up as: arrows worked on first
        // and last pages but not middle ones, where there's text in
        // the bottom band).
        //
        // Read the actual displayed canvas width and feed an
        // *effective* scale so the layer (and its spans) collapses to
        // the same dimensions the canvas paints at.
        const pdfjs = await loadPdfjs();
        const wrapRect = wrap.getBoundingClientRect();
        const displayWidth = wrapRect.width || cached.width;
        const cssScale = displayWidth / cached.width;
        const effectiveScale = scale * cssScale;
        textLayerEl.style.setProperty('--total-scale-factor', String(effectiveScale));
        // Clear any explicit width/height we may have set on prior
        // renders — pdfjs's setLayerDimensions will compute new ones
        // from the scale factor.
        textLayerEl.style.width = '';
        textLayerEl.style.height = '';
        const tl = new pdfjs.TextLayer({
          textContentSource: cached.textContent,
          container: textLayerEl,
          viewport: cached.viewport,
        });
        activeTextLayer = tl;
        try {
          await tl.render();
        } catch {
          /* cancelled by next render or by a page change; ignore */
        }

        if (snap.currentPage + 1 < snap.pageCount) c.prefetch(snap.currentPage + 1);
      })().catch(() => {
        spinner.textContent = 'failed to render page';
      });
    };

    cleanups.push(
      wireTextSelection(wrap, textLayerEl, redactBtn, store, modeState, proGate),
    );

    render();
    unsubscribe = store.subscribe(render);
  }

  if (!supportsManual) {
    // Text/CSV — no manual mode. Nothing to do; root has no toolbar.
  }

  return {
    root,
    dispose(): void {
      unsubscribe();
      for (const c of cleanups) c();
    },
  };
};

/** Esc exits manual mode at any time. */
const installEscapeExit = (state: { active: boolean }, exit: () => void): (() => void) => {
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && state.active) exit();
  };
  window.addEventListener('keydown', onKey);
  return () => {
    window.removeEventListener('keydown', onKey);
  };
};
